import type { AuditedCourseExpanded } from "@brookbush/types";

export const FREE_COURSE_COPY = {
	offerTitle: "Get this course free",
	offerBody:
		"Your free account includes one full course of your choice — every section and the final exam.",
	offerButton: "Use my free course",
	confirmTitle: (title: string) => `Use your free course on ${title}?`,
	confirmBody: "You can only choose one, and it stays unlocked.",
	confirmYes: "Unlock this course",
	confirmNo: "Not now",
	claimedBody:
		"This is your free course. Your certificate and CE credits unlock when you become a member.",
	usedBody: (title: string) =>
		`You've used your free course on ${title}. Become a member to unlock every course, certificate and CE credit.`,
	usedButton: "Become a member",
} as const;

export type FreeCourseState =
	| { kind: "none" }
	| { kind: "offer" }
	| { kind: "claimed" }
	| { kind: "used"; claimedTitle: string };

export type FreeCourseCopy = {
	title?: string;
	body: string;
	button?: string;
};

/**
 * What one course page shows for the free-course offer. A claimed course
 * stays visible, including for former members.
 *
 * @param {object} input
 * @param {boolean} input.isSignedIn - Whether the visitor is signed in
 * @param {boolean} input.isLoading - Access or audits still loading
 * @param {boolean} input.isMember - Current member
 * @param {boolean} input.isFormerMember - Canceled member
 * @param {boolean} input.isPaused - Paused member
 * @param {string} input.courseId - The course on this page
 * @param {AuditedCourseExpanded[] | undefined} input.audits - The user's free or granted courses
 * @returns {FreeCourseState} The state to render
 */
export const getFreeCourseState = (input: {
	isSignedIn: boolean;
	isLoading: boolean;
	isMember: boolean;
	isFormerMember: boolean;
	isPaused: boolean;
	courseId: string;
	audits: AuditedCourseExpanded[] | undefined;
}): FreeCourseState => {
	if (!input.isSignedIn || input.isLoading || input.isMember) {
		return { kind: "none" };
	}

	const audits = input.audits ?? [];

	if (audits.some((audit) => audit.courseId === input.courseId)) {
		return { kind: "claimed" };
	}

	if (input.isFormerMember || input.isPaused) {
		return { kind: "none" };
	}

	const [claimed] = audits;

	return claimed
		? { kind: "used", claimedTitle: claimed.course.title }
		: { kind: "offer" };
};

/**
 * Card copy for a free-course state.
 *
 * @param {FreeCourseState} state - From `getFreeCourseState`
 * @returns {FreeCourseCopy | null} Null when nothing should show
 */
export const getFreeCourseCopy = (
	state: FreeCourseState,
): FreeCourseCopy | null => {
	switch (state.kind) {
		case "offer":
			return {
				title: FREE_COURSE_COPY.offerTitle,
				body: FREE_COURSE_COPY.offerBody,
				button: FREE_COURSE_COPY.offerButton,
			};
		case "claimed":
			return { body: FREE_COURSE_COPY.claimedBody };
		case "used":
			return {
				body: FREE_COURSE_COPY.usedBody(state.claimedTitle),
				button: FREE_COURSE_COPY.usedButton,
			};
		default:
			return null;
	}
};

/**
 * Whether every section of a course may be opened.
 *
 * @param {object} input
 * @param {boolean} input.isMember - Current member
 * @param {AuditedCourseExpanded[] | undefined} input.audits - The user's free or granted courses
 * @param {string} input.courseId - The course on this page
 * @returns {boolean} True for members and free or granted courses
 */
export const hasFullCourseAccess = (input: {
	isMember: boolean;
	audits: AuditedCourseExpanded[] | undefined;
	courseId: string;
}): boolean =>
	input.isMember ||
	!!input.audits?.some((audit) => audit.courseId === input.courseId);

/**
 * Whether to open the claim prompt on its own: right after signing up from
 * this course, once per course per session.
 *
 * @param {object} input
 * @param {FreeCourseState} input.state - From `getFreeCourseState`
 * @param {unknown} input.signupCourseId - Course clicked before signing up
 * @param {string} input.courseId - The course on this page
 * @param {boolean} input.dismissed - Already prompted this session
 * @returns {boolean} True to open the prompt
 */
export const shouldPromptAfterSignup = (input: {
	state: FreeCourseState;
	signupCourseId: unknown;
	courseId: string;
	dismissed: boolean;
}): boolean =>
	input.state.kind === "offer" &&
	input.signupCourseId === input.courseId &&
	!input.dismissed;

/** sessionStorage key that stops the post-signup prompt reopening. */
export const freeCoursePromptKey = (courseId: string) =>
	`free-course-prompted-${courseId}`;
